/**
 * CameraStreamModal
 *
 * Modal wrapper that displays a camera's live stream and recordings.
 * Uses the existing CameraDetail component from CANVMS.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { CameraLocation } from './types';
import CameraDetail from '../../../CANvision/components/CameraDetail/CameraDetail';
import { getStreamUrl } from '../../../../../api/canvms';

interface CameraStreamModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Camera to display */
  camera: CameraLocation | null;
  /** Callback when the modal is closed */
  onClose: () => void;
}

/**
 * Modal for viewing camera live stream and recordings.
 * Fetches the stream URL and renders CameraDetail.
 */
const CameraStreamModal: React.FC<CameraStreamModalProps> = ({
  open,
  camera,
  onClose,
}) => {
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [streamType, setStreamType] = useState<'hls' | 'jpeg' | 'mjpeg'>('hls');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch stream URL when camera changes
  useEffect(() => {
    if (!open || !camera) {
      setStreamUrl(null);
      setSnapshotUrl(null);
      setError(null);
      return;
    }

    const fetchStreamInfo = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Determine stream type from camera
        const type = camera.stream_type === 'jpeg' ? 'mjpeg' : 'hls';
        const response = await getStreamUrl(camera.site_id, camera.id, type);

        setStreamUrl(response.data?.stream_url || null);
        setSnapshotUrl(response.data?.snapshot_url || null);
        setStreamType(camera.stream_type || 'hls');
      } catch (err) {
        console.error('[CameraStreamModal] Error fetching stream URL:', err);
        setStreamUrl(null);
        setError('Live video unavailable');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreamInfo();
  }, [open, camera]);

  if (!camera) return null;

  const displayName = camera.display_name || camera.name || 'Camera';

  // Show a lightweight error dialog when the stream URL cannot be fetched
  if (error && !streamUrl) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <VideocamOffIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {displayName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // Convert CameraLocation to the format CameraDetail expects
  const cameraForDetail = {
    id: camera.id,
    name: camera.name,
    display_name: camera.display_name,
    ptz_capable: camera.ptz_capable,
  };

  return (
    <CameraDetail
      open={open}
      onClose={onClose}
      camera={cameraForDetail}
      siteId={camera.site_id}
      liveStreamUrl={streamUrl}
      snapshotUrl={snapshotUrl}
      streamType={streamType}
    />
  );
};

export default CameraStreamModal;
